import type {
  EventCheckout,
  EventCheckoutRequest,
  EventPeriod,
  EventQuoteRequest,
  EventSummary,
  PriceQuote,
  ProviderCheckInResult,
  ProviderEventAssignment,
  SlotHoldResult
} from "@kuroo/contracts";
import type { EventAttendee, EventTicket } from "./client-catalog-types";

export type SdkEventsContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function events(context: SdkEventsContext): Promise<EventSummary[]> {
  return context.request<EventSummary[]>("/api/v1/events");
}

export function createEvent(context: SdkEventsContext, input: { name: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"]; categoryId?: string }): Promise<Pick<EventSummary, "id" | "name" | "slug" | "status">> {
  return context.request<Pick<EventSummary, "id" | "name" | "slug" | "status">>("/api/v1/events", { method: "POST", body: JSON.stringify(input) });
}

export function event(context: SdkEventsContext, eventId: string): Promise<EventSummary> {
  return context.request<EventSummary>(`/api/v1/events/${encodeURIComponent(eventId)}`);
}

export function eventProviders(context: SdkEventsContext, eventId: string): Promise<Array<{ id: string; displayName: string; status: string }>> {
  return context.request<Array<{ id: string; displayName: string; status: string }>>(`/api/v1/events/${encodeURIComponent(eventId)}/providers`);
}

export function saveEventProviders(context: SdkEventsContext, eventId: string, providerIds: string[]): Promise<Array<{ id: string; displayName: string; status: string }>> {
  return context.request<Array<{ id: string; displayName: string; status: string }>>(`/api/v1/events/${encodeURIComponent(eventId)}/providers`, { method: "PUT", body: JSON.stringify({ providerIds }) });
}

export function updateEvent(context: SdkEventsContext, eventId: string, input: { name?: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"] }): Promise<EventSummary> {
  return context.request<EventSummary>(`/api/v1/events/${encodeURIComponent(eventId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteEvent(context: SdkEventsContext, eventId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
  return context.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/events/${encodeURIComponent(eventId)}`, { method: "DELETE" });
}

export function cancelEvent(context: SdkEventsContext, eventId: string): Promise<{ id: string; status: "cancelled"; cancelledAttendees: number }> {
  return context.request<{ id: string; status: "cancelled"; cancelledAttendees: number }>(`/api/v1/events/${encodeURIComponent(eventId)}/cancel`, { method: "POST", body: "{}" });
}

export function eventPeriods(context: SdkEventsContext, eventId: string): Promise<EventPeriod[]> {
  return context.request<EventPeriod[]>(`/api/v1/events/${encodeURIComponent(eventId)}/periods`);
}

export function quoteEvent(context: SdkEventsContext, input: EventQuoteRequest): Promise<PriceQuote> {
  return context.request<PriceQuote>("/api/v1/events/quote", { method: "POST", body: JSON.stringify(input), headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
}

/** Calculates and serializes the capacity for an event proposal in one operation. */
export function quoteAndHoldEvent(context: SdkEventsContext, input: EventQuoteRequest & { idempotencyKey: string }): Promise<SlotHoldResult> {
  return context.request<SlotHoldResult>("/api/v1/events/quote-and-hold", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function createEventCheckout(context: SdkEventsContext, input: EventCheckoutRequest): Promise<EventCheckout> {
  return context.request<EventCheckout>("/api/v1/events/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function eventOrder(context: SdkEventsContext, orderId: string): Promise<EventCheckout["order"]> {
  return context.request<EventCheckout["order"]>(`/api/v1/events/orders/${encodeURIComponent(orderId)}`);
}

export function createEventPeriod(context: SdkEventsContext, input: { eventId: string; startsAt: string; endsAt: string; locationId?: string; capacity: number }): Promise<EventPeriod> {
  return context.request<EventPeriod>("/api/v1/events/periods", { method: "POST", body: JSON.stringify(input) });
}

export function updateEventPeriod(context: SdkEventsContext, periodId: string, input: { startsAt?: string; endsAt?: string; locationId?: string | null; capacity?: number; status?: "open" | "closed" | "cancelled" }): Promise<EventPeriod & { cancelledAttendees?: number }> {
  return context.request<EventPeriod & { cancelledAttendees?: number }>(`/api/v1/events/periods/${encodeURIComponent(periodId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteEventPeriod(context: SdkEventsContext, periodId: string): Promise<{ id: string; eventId: string; deleted: boolean }> {
  return context.request<{ id: string; eventId: string; deleted: boolean }>(`/api/v1/events/periods/${encodeURIComponent(periodId)}`, { method: "DELETE" });
}

export function eventTickets(context: SdkEventsContext, eventId: string): Promise<EventTicket[]> {
  return context.request<EventTicket[]>(`/api/v1/events/${encodeURIComponent(eventId)}/tickets`);
}

export function createEventTicket(context: SdkEventsContext, input: { eventId: string; name: string; description?: string; priceCents?: number; capacity?: number }): Promise<{ id: string; eventId: string; name: string }> {
  return context.request<{ id: string; eventId: string; name: string }>("/api/v1/events/tickets", { method: "POST", body: JSON.stringify(input) });
}

export function updateEventTicket(context: SdkEventsContext, ticketId: string, input: { name?: string; description?: string | null; priceCents?: number; capacity?: number; status?: "active" | "inactive" }): Promise<{ id: string; eventId: string; name: string; description?: string | null; priceCents: number; capacity: number; status: "active" | "inactive" }> {
  return context.request<{ id: string; eventId: string; name: string; description?: string | null; priceCents: number; capacity: number; status: "active" | "inactive" }>(`/api/v1/events/tickets/${encodeURIComponent(ticketId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteEventTicket(context: SdkEventsContext, ticketId: string): Promise<{ id: string; eventId: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; eventId: string; status: "inactive"; deleted: boolean }>(`/api/v1/events/tickets/${encodeURIComponent(ticketId)}`, { method: "DELETE" });
}

export function createEventAttendee(context: SdkEventsContext, input: { periodId: string; ticketId?: string; bookingId?: string; name: string; email?: string; phone?: string; idempotencyKey?: string }): Promise<EventAttendee & { qrToken?: string }> {
  return context.request<EventAttendee & { qrToken?: string }>("/api/v1/events/attendees", { method: "POST", body: JSON.stringify(input) });
}

export function eventAttendees(context: SdkEventsContext, periodId: string): Promise<EventAttendee[]> {
  return context.request<EventAttendee[]>(`/api/v1/events/periods/${encodeURIComponent(periodId)}/attendees`);
}

export function providerEvents(context: SdkEventsContext): Promise<ProviderEventAssignment[]> {
  return context.request<ProviderEventAssignment[]>("/api/v1/provider/events");
}

export function checkInEventAttendee(context: SdkEventsContext, qrToken: string): Promise<ProviderCheckInResult> {
  return context.request<ProviderCheckInResult>("/api/v1/events/attendees/check-in", { method: "POST", body: JSON.stringify({ qrToken }) });
}

export function cancelEventAttendee(context: SdkEventsContext, attendeeId: string): Promise<{ id: string; status: "cancelled" }> {
  return context.request<{ id: string; status: "cancelled" }>(`/api/v1/events/attendees/${encodeURIComponent(attendeeId)}/cancel`, { method: "POST", body: "{}" });
}
